/** A run of comment text: either a resolved `@Name` mention or plain text. */
export interface MentionRun {
  text: string;
  mention: boolean;
}

/**
 * ASCII punctuation — every character that can legitimately follow a mention
 * without being part of the name (`@Alice's`, `@Alice,`, `@Alice.`).
 * Deliberately a Set rather than a character-class regex: nothing here is ever
 * matched against user-supplied text, so there is no regex to reason about.
 */
const PUNCTUATION = new Set("!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~");

/**
 * A name match must end at a word boundary: `@AliceSmithson` is not `@Alice`.
 * The end of the body, whitespace (any script) and punctuation all close a
 * mention; anything else — a letter or digit in any script — continues it.
 */
function endsMention(ch: string | undefined): boolean {
  if (!ch) return true;
  return ch.trim() === "" || PUNCTUATION.has(ch);
}

/**
 * Split a comment body into plain and `@<DisplayName>` runs (WHA-3311). Names
 * come from the project member list, so only real users highlight — random
 * `@foo` text stays plain. Longest name first, so "Alice Smith" wins over
 * "Alice" when both are members.
 */
export function splitMentionRuns(body: string, displayNames: string[]): MentionRun[] {
  const names = displayNames.filter(Boolean).sort((a, b) => b.length - a.length);
  if (names.length === 0) return body ? [{ text: body, mention: false }] : [];
  const runs: MentionRun[] = [];
  let plain = "";
  let i = 0;
  while (i < body.length) {
    const hit =
      body[i] === "@"
        ? names.find((n) => body.startsWith(n, i + 1) && endsMention(body[i + 1 + n.length]))
        : undefined;
    if (hit) {
      if (plain) {
        runs.push({ text: plain, mention: false });
        plain = "";
      }
      runs.push({ text: `@${hit}`, mention: true });
      i += hit.length + 1;
    } else {
      plain += body[i];
      i += 1;
    }
  }
  if (plain) runs.push({ text: plain, mention: false });
  return runs;
}
